<script setup lang="ts">
import { ref, onUnmounted } from 'vue';
import BpmnService, { GroupExecutionState } from '@/modules/low-code/process/services/BpmnService';
import { RouterLink } from 'vue-router';

const emit = defineEmits(["completed", "error"]);

const props = defineProps({
  "licence": null
});

const stateId = ref<string | null>(null);
const pollIntervalMs = ref<number>(1000);
const state = ref<GroupExecutionState | null>(null);
const isPolling = ref<boolean>(false);
const isCompleted = ref<boolean>(false);
let intervalHandle: number | null = null;

// Function to start polling
const start = async (id: string, interval: number = 1000) =>
{
    stop();
    stateId.value = id;
    pollIntervalMs.value = interval;
    isCompleted.value = false;
    state.value = { total: 0, processed: 0, errors: [] };
    isPolling.value = true;

    intervalHandle = window.setInterval(poll, pollIntervalMs.value);
    await poll();
};

// Function to stop polling
const stop = () =>
{
    if (intervalHandle)
    {
        clearInterval(intervalHandle);
        intervalHandle = null;
    }

    isPolling.value = false;
};

// Function to poll the execution state
const poll = async () =>
{
    if (!stateId.value) return;

    try
    {
        state.value = await BpmnService.getGroupExecutionState(stateId.value);

        if (state.value.processed >= state.value.total && state.value.total > 0)
        {
            stop();
            isCompleted.value = true;
            emit('completed', state.value);
        }
    }
    catch (ex)
    {
        stop();
        // propagate error but keep the last state visible
        emit('error', ex);
    }
};

onUnmounted(() =>
{
    stop();
});

defineExpose({
    start,
    stop
});

</script>

<template>
    <div v-if="state">
        <IdeoFormGroup :label="$t('[[[Wykonywanie akcji]]]')">
            <div class="progress mb-2">
                <div
                    class="progress-bar"
                    :style="{ width: state.total > 0 ? (state.processed / state.total * 100) + '%' : '0%' }"
                ></div>
            </div>
            <div class="d-flex align-items-center justify-content-between">
                <small class="text-muted">
                    {{ state.processed }} / {{ state.total }} {{ $t('[[[dokumentów przetworzonych]]]') }}
                </small>
                <small v-if="isCompleted" class="text-success">
                    {{ $t('[[[Zakończono]]]') }}
                </small>
            </div>
        </IdeoFormGroup>
        <div v-if="state.errors && state.errors.length" class="mt-3">
            <h3>{{ $t('[[[Błędy]]]') }}</h3>
            <div style="max-height: 400px;" class="overflow-auto">
                <table class="table h-100">
                    <thead>
                        <tr>
                            <th scope="col">{{ $t('[[[Dokument]]]') }}</th>
                            <th scope="col">{{ $t('[[[Błąd]]]') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(err, idx) in state.errors" :key="idx">
                            <td>
                                <RouterLink :to="`/module/${licence}/details/${err.docId}`">
                                    {{ err.docSignature || err.docId }}
                                </RouterLink>
                            </td>
                            <td>{{ err.error }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <hr />
    </div>
</template>
